import { useEffect, useRef } from 'react';
import { HOLO_MODEL, holoFrame } from '@/ui/holo';
import { TongueScene } from '@/ui/tongue3d/TongueScene';
import styles from './AlexLab.module.css';

/**
 * Alex's scope (the Peek-Inside Scope / X-Scope): his hologram projector, with the live 3D mouth
 * floating above it. While `on`, the light rises and the mouth loops gently from rest to the
 * bunched r and back, turning to show it from other angles; off, only the projector stands there.
 */
export function ScopeView({ on, label }: { on: boolean; label: string }) {
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = host.current;
    if (!el || !on) return;
    const scene = new TongueScene(el, { orbitControls: false });
    scene.setBlend('rest', 'rest', 1);
    const run = { stopped: false };
    const stopped = () => run.stopped;
    const pause = (ms: number) =>
      new Promise((resolve) => {
        setTimeout(resolve, ms);
      });
    void (async () => {
      await scene.ready;
      while (!stopped()) {
        await scene.moveTo('rBunched', 1400);
        if (stopped()) break;
        await scene.tour(5000, 0.18);
        if (stopped()) break;
        await scene.moveTo('rest', 1200);
        await pause(1500);
      }
    })();
    return () => {
      run.stopped = true;
      scene.dispose();
    };
  }, [on]);

  const frame = holoFrame(on);
  return (
    <div
      className={`${styles.scope ?? ''} ${frame.className}`}
      style={frame.style}
      role="img"
      aria-label={label}
    >
      <div ref={host} className={HOLO_MODEL} />
    </div>
  );
}
